/**
 * Role / staff_type-driven navigation (Prompt 11E.5).
 *
 * Single source of truth for which nav items a signed-in user sees, in what
 * order. Consumed by `Sidebar.tsx` (icon keys map to the existing inline SVG
 * icons there — this file stays framework-agnostic / no JSX).
 *
 * Hiding here is a UX convenience ONLY — every path below is additionally
 * enforced server-side (see `backend/app/core/auth/scoping.py` and the
 * `require_role(...)` gates on each router). A route missing from this list
 * for a given persona still 403s if the user navigates to it directly; see
 * `src/routes/index.tsx` for the route-level guards.
 *
 * Persona resolution (documented decision — 4 personas per the 11E.5 spec).
 * This MUST mirror `backend/app/core/auth/scoping.py`, which decides the
 * same question server-side; a nav that shows pages the server scopes to
 * nothing renders as a set of permanently empty screens.
 *
 *   - `owner` | `admin`                              → Owner/Admin nav
 *   - `viewer`                                        → Viewer (portal) nav
 *   - `manager`                                       → Office Manager nav
 *   - `staff` AND `staff_type === 'office_manager'`   → Office Manager nav
 *     (server leaves these callers UNSCOPED — front-desk staff work across
 *     the whole tenant)
 *   - `staff` with any other staff_type (teacher |
 *     other | null | unclassified)                    → Teacher nav
 *     (server scopes these callers to their own assigned students, so the
 *     teacher nav is the only set of pages that actually returns data —
 *     unclassified staff fail closed into the restrictive persona)
 */

import type { Role, StaffType } from '@/types';

export type NavIconKey =
  | 'dashboard'
  | 'myDay'
  | 'students'
  | 'myStudents'
  | 'staff'
  | 'calendar'
  | 'bookings'
  | 'reschedules'
  | 'leaves'
  | 'invoices'
  | 'paymentsDue'
  | 'expenses'
  | 'memberships'
  | 'subjects'
  | 'packages'
  | 'reports'
  | 'reportsGrades'
  | 'settings'
  | 'requests'
  | 'attendance'
  | 'dataQuality';

export type NavGroupKey = 'overview' | 'people' | 'academics' | 'billing' | 'operations' | 'admin';

export interface NavItemConfig {
  path: string;
  label: string;
  icon: NavIconKey;
  /** Optional — ungrouped items render under "Overview". Added for 11F.2 sidebar sectioning. */
  group?: NavGroupKey;
}

export interface NavGroupMeta {
  key: NavGroupKey;
  label: string;
  order: number;
}

export const NAV_GROUPS: NavGroupMeta[] = [
  { key: 'overview', label: 'Overview', order: 0 },
  { key: 'people', label: 'People', order: 1 },
  { key: 'academics', label: 'Academics', order: 2 },
  { key: 'billing', label: 'Billing', order: 3 },
  { key: 'operations', label: 'Operations', order: 4 },
  { key: 'admin', label: 'Admin', order: 5 },
];

export type NavPersona = 'owner_admin' | 'teacher' | 'office_manager' | 'viewer';

// ---------------------------------------------------------------------------
// Persona-specific nav sets (order matters — matches the 11E.5 spec order)
// ---------------------------------------------------------------------------

const OWNER_ADMIN_NAV: NavItemConfig[] = [
  { path: '/', label: 'Dashboard', icon: 'dashboard', group: 'overview' },
  { path: '/students', label: 'Students', icon: 'students', group: 'people' },
  { path: '/staff', label: 'Staff', icon: 'staff', group: 'people' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar', group: 'academics' },
  { path: '/bookings', label: 'Bookings', icon: 'bookings', group: 'academics' },
  { path: '/invoices', label: 'Billing/Invoices', icon: 'invoices', group: 'billing' },
  // 11F.7 — payments were previously only reachable from inside an invoice.
  { path: '/payments', label: 'Payments', icon: 'paymentsDue', group: 'billing' },
  { path: '/expenses', label: 'Expenses', icon: 'expenses', group: 'billing' },
  { path: '/admin/memberships', label: 'Memberships', icon: 'memberships', group: 'billing' },
  { path: '/subjects', label: 'Subjects', icon: 'subjects', group: 'academics' },
  { path: '/packages', label: 'Packages', icon: 'packages', group: 'academics' },
  // 11F.6 — Leave approvals folded into the Requests hub as a sub-tab
  // (owner/admin/manager see it there; see src/routes/requestsHub.tsx) —
  // no longer a standalone nav item. Staff-attendance summary remains
  // separate (it's a report, not a request queue).
  { path: '/admin/requests', label: 'Requests', icon: 'requests', group: 'operations' },
  { path: '/admin/staff-attendance', label: 'Staff Attendance', icon: 'attendance', group: 'operations' },
  { path: '/reports', label: 'Reports', icon: 'reports', group: 'operations' },
  // 11F.1 — data-quality abnormality queue (migration gaps today; any future
  // import/reconciliation job going forward). Owner/admin only — mirrors
  // backend QUEUE_ROLES in app/features/data_quality/config/rules.py.
  { path: '/admin/data-quality', label: 'Data Quality', icon: 'dataQuality', group: 'admin' },
  { path: '/settings', label: 'Settings', icon: 'settings', group: 'admin' },
];

// Teacher (staff_type='teacher') — ordered by daily relevance per spec.
const TEACHER_NAV: NavItemConfig[] = [
  { path: '/', label: 'My Day', icon: 'myDay', group: 'overview' },
  { path: '/students', label: 'My Students', icon: 'myStudents', group: 'people' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar', group: 'academics' },
  { path: '/admin/requests', label: 'Reschedules', icon: 'reschedules', group: 'operations' },
  { path: '/my-leaves', label: 'My Leaves & Attendance', icon: 'leaves', group: 'operations' },
  { path: '/reports', label: 'Reports (Grades)', icon: 'reportsGrades', group: 'operations' },
];

// Office manager — no Salary visibility, no Settings.
const OFFICE_MANAGER_NAV: NavItemConfig[] = [
  { path: '/students', label: 'Students', icon: 'students', group: 'people' },
  { path: '/invoices', label: 'Payments Due', icon: 'paymentsDue', group: 'billing' },
  { path: '/expenses', label: 'Expenses', icon: 'expenses', group: 'billing' },
  { path: '/bookings', label: 'Bookings', icon: 'bookings', group: 'academics' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar', group: 'academics' },
  { path: '/admin/memberships', label: 'Memberships', icon: 'memberships', group: 'billing' },
  { path: '/admin/requests', label: 'Requests', icon: 'requests', group: 'operations' },
  { path: '/reports', label: 'Reports', icon: 'reports', group: 'operations' },
];

// Viewer (guardian/student portal). "Attendance" temporarily reuses the
// existing Sessions page (which already surfaces per-session attendance
// status) pending a dedicated attendance tab — tracked as a follow-up, not
// a silent scope cut (no separate attendance data source exists yet).
const VIEWER_NAV: NavItemConfig[] = [
  { path: '/portal/sessions', label: 'My Classes/Calendar', icon: 'calendar', group: 'overview' },
  { path: '/portal/sessions', label: 'Attendance', icon: 'attendance', group: 'academics' },
  { path: '/portal/balance', label: 'Memberships & Payments', icon: 'memberships', group: 'billing' },
  { path: '/portal/grades', label: 'Grades', icon: 'reportsGrades', group: 'academics' },
  { path: '/portal/requests', label: 'Requests', icon: 'requests', group: 'operations' },
];

// ---------------------------------------------------------------------------
// Persona resolution + public API
// ---------------------------------------------------------------------------

export function resolveNavPersona(role: Role, staffType: StaffType | null): NavPersona {
  if (role === 'owner' || role === 'admin') return 'owner_admin';
  if (role === 'viewer') return 'viewer';
  if (role === 'staff') {
    // Only an explicit office_manager classification widens a staff account
    // to the tenant-wide nav — this is the exact condition under which
    // get_teacher_scope() returns None (unscoped) on the backend. Everyone
    // else (teacher | other | null | unrecognised) is teacher-scoped
    // server-side and gets the teacher nav, which is the restrictive set.
    return staffType === 'office_manager' ? 'office_manager' : 'teacher';
  }
  // `manager` — a distinct role that the backend never scopes.
  return 'office_manager';
}

const NAV_BY_PERSONA: Record<NavPersona, NavItemConfig[]> = {
  owner_admin: OWNER_ADMIN_NAV,
  teacher: TEACHER_NAV,
  office_manager: OFFICE_MANAGER_NAV,
  viewer: VIEWER_NAV,
};

/** Config-driven nav item list for a signed-in user's role + staff_type. */
export function getNavItems(role: Role, staffType: StaffType | null): NavItemConfig[] {
  return NAV_BY_PERSONA[resolveNavPersona(role, staffType)];
}

export interface GroupedNavItems {
  group: NavGroupMeta;
  items: NavItemConfig[];
}

/**
 * Same items as getNavItems, bucketed into sections for the sidebar (11F.2).
 * Ungrouped items (no `group` field) fall into "Overview". Empty groups are
 * dropped so a persona's nav never shows a section header with nothing
 * under it.
 */
export function getGroupedNavItems(role: Role, staffType: StaffType | null): GroupedNavItems[] {
  const items = getNavItems(role, staffType);
  const byGroup = new Map<NavGroupKey, NavItemConfig[]>();
  for (const item of items) {
    const key = item.group ?? 'overview';
    const bucket = byGroup.get(key);
    if (bucket) bucket.push(item);
    else byGroup.set(key, [item]);
  }
  return NAV_GROUPS.filter((g) => byGroup.has(g.key)).map((group) => ({
    group,
    items: byGroup.get(group.key) ?? [],
  }));
}

